import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, apiGet, apiPost } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

interface GbpAccount {
  name: string;
  accountName: string;
  type: string;
  role: string;
}

interface GbpLocation {
  name: string;
  title: string;
  storeCode?: string | null;
  primaryCategoryDisplayName?: string | null;
  primaryPhone?: string | null;
  websiteUri?: string | null;
  storefrontAddress?: {
    addressLines?: string[];
    locality?: string;
    administrativeArea?: string;
    postalCode?: string;
    regionCode?: string;
  };
  metadata?: { placeId?: string };
}

function formatAddress(addr: GbpLocation["storefrontAddress"]): string | null {
  if (!addr) return null;
  const parts: string[] = [];
  if (addr.addressLines?.length) parts.push(...addr.addressLines.filter(Boolean));
  const cityState = [addr.locality, addr.administrativeArea]
    .filter(Boolean)
    .join(", ");
  if (cityState) parts.push(cityState);
  if (addr.postalCode) parts.push(addr.postalCode);
  return parts.length ? parts.join(", ") : null;
}

function slugifyForGroup(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function OnboardingGbpPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialError = searchParams.get("error");
  const isSuper = user?.role === "super_admin";

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-8 sm:py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Onboard a dealer group from Google Business Profile
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Connect your Google account, pick which Business Profile account holds the group's rooftops, then select the locations to import.
        </p>
      </header>

      {!isSuper && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Onboarding is restricted to super-admin accounts.
        </div>
      )}

      {isSuper && initialError && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          OAuth error: {initialError}
        </div>
      )}

      {isSuper && <OnboardingWizard />}
    </div>
  );
}

function OnboardingWizard() {
  const [sessionInfo, setSessionInfo] = useState<{
    googleEmail: string;
    isFixture: boolean;
  } | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ googleEmail: string; isFixture: boolean }>(
      "/api/onboarding/gbp/session"
    )
      .then((s) => {
        if (cancelled) return;
        setSessionInfo(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 409 = no onboarding session yet. Just stay on Step 1.
        if (err instanceof ApiError && err.status === 409) {
          setSessionInfo(null);
        } else {
          console.error("onboarding/session load failed:", err);
        }
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (sessionLoading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }

  if (!sessionInfo) {
    return <Step1Connect />;
  }
  return <Step2Pick sessionInfo={sessionInfo} />;
}

function Step1Connect() {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">
        Step 1 · Connect Google
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        You'll sign in with the Google account that manages the dealer group's Business Profiles. Savvy Reputation will read your accessible accounts + locations — nothing is created in Google.
      </p>
      <a
        href="/oauth/gbp/onboarding/start"
        className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-brand-700"
      >
        Connect Google Business Profile
      </a>
    </div>
  );
}

function Step2Pick({
  sessionInfo,
}: {
  sessionInfo: { googleEmail: string; isFixture: boolean };
}) {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<GbpAccount[]>([]);
  const [accountsErr, setAccountsErr] = useState<string | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(
    null
  );
  const [locations, setLocations] = useState<GbpLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [checkedLocations, setCheckedLocations] = useState<Set<string>>(
    new Set()
  );
  const [groupName, setGroupName] = useState("");
  const [groupSlug, setGroupSlug] = useState("");
  const [groupSlugDirty, setGroupSlugDirty] = useState(false);
  const [seedFixtureReviews, setSeedFixtureReviews] = useState(
    sessionInfo.isFixture
  );
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ accounts: GbpAccount[] }>("/api/onboarding/gbp/accounts")
      .then((d) => {
        if (cancelled) return;
        setAccounts(d.accounts);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAccountsErr(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingAccounts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAccountName) {
      setLocations([]);
      return;
    }
    let cancelled = false;
    setLoadingLocations(true);
    const accountId = selectedAccountName.replace(/^accounts\//, "");
    apiGet<{ locations: GbpLocation[] }>(
      `/api/onboarding/gbp/accounts/${encodeURIComponent(accountId)}/locations`
    )
      .then((d) => {
        if (cancelled) return;
        setLocations(d.locations);
        setCheckedLocations(new Set(d.locations.map((l) => l.name)));
        // Seed a default group name from the chosen account when blank.
        if (!groupName) {
          const chosen = accounts.find((a) => a.name === selectedAccountName);
          if (chosen) {
            setGroupName(chosen.accountName);
            if (!groupSlugDirty) {
              setGroupSlug(slugifyForGroup(chosen.accountName));
            }
          }
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setImportErr(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingLocations(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountName]);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => a.title.localeCompare(b.title)),
    [locations]
  );

  function toggleLocation(name: string) {
    setCheckedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll(value: boolean) {
    if (value) setCheckedLocations(new Set(sortedLocations.map((l) => l.name)));
    else setCheckedLocations(new Set());
  }

  async function handleImport() {
    if (!selectedAccountName || checkedLocations.size === 0) return;
    if (!groupName.trim() || !groupSlug.trim()) {
      setImportErr("Group name and slug are required.");
      return;
    }
    setImporting(true);
    setImportErr(null);
    try {
      const chosenLocations = sortedLocations
        .filter((l) => checkedLocations.has(l.name))
        .map((l) => ({
          name: l.name,
          title: l.title,
          storeCode: l.storeCode ?? null,
          primaryCategoryDisplayName: l.primaryCategoryDisplayName ?? null,
          primaryPhone: l.primaryPhone ?? null,
          websiteUri: l.websiteUri ?? null,
          placeId: l.metadata?.placeId ?? null,
          address: formatAddress(l.storefrontAddress),
        }));
      const result = await apiPost<{
        groupId: string;
        imported: Array<{ dealerId: string; slug: string; title: string }>;
      }>("/api/onboarding/gbp/import", {
        groupName: groupName.trim(),
        groupSlug: groupSlug.trim(),
        accountName: selectedAccountName,
        locations: chosenLocations,
        seedFixtureReviews,
      });
      // Force a hard reload so ScopeContext fetches fresh dealers+groups +
      // localStorage focus picks up the new group.
      try {
        localStorage.setItem("savvy.rep.focusedGroupId", result.groupId);
        localStorage.removeItem("savvy.rep.focusedDealerId");
      } catch {
        /* noop */
      }
      navigate("/reviews", { replace: true });
      window.location.reload();
    } catch (err) {
      setImportErr(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        Signed in to Google as <strong>{sessionInfo.googleEmail}</strong>
        {sessionInfo.isFixture && (
          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-amber-900">
            fixture session
          </span>
        )}
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900">
          Step 2 · Pick the Business Profile account
        </h2>
        {loadingAccounts && (
          <div className="mt-3 text-sm text-slate-500">Loading accounts…</div>
        )}
        {accountsErr && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {accountsErr}
          </div>
        )}
        {!loadingAccounts && accounts.length === 0 && !accountsErr && (
          <div className="mt-3 text-sm text-slate-500">
            No Business Profile accounts found for this Google login. Make sure the Google account has manager/owner access to the dealer group.
          </div>
        )}
        {!loadingAccounts && accounts.length > 0 && (
          <ul className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200">
            {accounts.map((a) => {
              const isSelected = selectedAccountName === a.name;
              return (
                <li key={a.name}>
                  <button
                    type="button"
                    onClick={() => setSelectedAccountName(a.name)}
                    className={[
                      "flex w-full items-center justify-between px-4 py-3 text-left text-sm",
                      isSelected ? "bg-brand-50" : "hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <div>
                      <div className="font-medium text-slate-900">{a.accountName}</div>
                      <div className="text-xs text-slate-500">
                        {a.type} · {a.role} · {a.name}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        selected
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {selectedAccountName && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">
            Step 3 · Pick locations &amp; name the group
          </h2>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-xs font-medium text-slate-600">
                Group name
              </span>
              <input
                value={groupName}
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (!groupSlugDirty) {
                    setGroupSlug(slugifyForGroup(e.target.value));
                  }
                }}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="Alan Jay Auto Group"
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs font-medium text-slate-600">
                Group slug
              </span>
              <input
                value={groupSlug}
                onChange={(e) => {
                  setGroupSlug(slugifyForGroup(e.target.value));
                  setGroupSlugDirty(true);
                }}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="alan-jay-auto-group"
              />
            </label>
          </div>

          {loadingLocations && (
            <div className="mt-4 text-sm text-slate-500">Loading locations…</div>
          )}

          {!loadingLocations && sortedLocations.length === 0 && (
            <div className="mt-4 text-sm text-slate-500">
              No locations on this account.
            </div>
          )}

          {!loadingLocations && sortedLocations.length > 0 && (
            <>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
                <span>
                  {checkedLocations.size} of {sortedLocations.length} selected
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="underline hover:text-slate-900"
                    onClick={() => toggleAll(true)}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="underline hover:text-slate-900"
                    onClick={() => toggleAll(false)}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <ul className="mt-2 max-h-96 divide-y divide-slate-200 overflow-y-auto rounded-md border border-slate-200">
                {sortedLocations.map((l) => {
                  const checked = checkedLocations.has(l.name);
                  const addr = formatAddress(l.storefrontAddress);
                  return (
                    <li key={l.name}>
                      <label
                        className={[
                          "flex cursor-pointer items-start gap-3 px-4 py-3 text-sm",
                          checked ? "bg-brand-50/60" : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleLocation(l.name)}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900">{l.title}</div>
                          <div className="truncate text-xs text-slate-500">
                            {[addr, l.primaryCategoryDisplayName]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {sessionInfo.isFixture && (
            <label className="mt-4 flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={seedFixtureReviews}
                onChange={(e) => setSeedFixtureReviews(e.target.checked)}
              />
              Seed 15 fixture reviews per store for local demo data
            </label>
          )}

          {importErr && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {importErr}
            </div>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              disabled={
                importing ||
                checkedLocations.size === 0 ||
                !groupName.trim() ||
                !groupSlug.trim()
              }
              onClick={() => void handleImport()}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {importing
                ? "Importing…"
                : `Import ${checkedLocations.size} ${
                    checkedLocations.size === 1 ? "store" : "stores"
                  }`}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
